import React, { useState } from "react";
import { decision } from "../lib/api";
import { toast } from "sonner";
import { Plus, Scale, Trash2 } from "lucide-react";
import RiskBadge from "../components/RiskBadge";

export default function DecisionPage() {
  const [situation, setSituation] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!situation.trim() || opts.length < 1) {
      toast.error("Add a little context and at least one option");
      return;
    }
    setBusy(true);
    setR(null);
    try {
      const res = await decision.ask(situation, opts);
      setR(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Let’s try that again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-7">
      <div className="fade-in-up">
        <h1 className="font-display text-3xl text-[#2E2E2E]">Weigh a decision</h1>
        <p className="text-sm text-[#6B6B6B] mt-1.5 leading-relaxed">Lay it out gently. Wisdom needs room to breathe.</p>
      </div>

      <form onSubmit={run} className="rounded-3xl border border-[#DCE5DB] bg-white p-6 space-y-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up stagger-1">
        <div>
          <label className="block text-sm text-[#6B6B6B] mb-2">What’s the situation?</label>
          <textarea
            data-testid="decision-situation"
            rows={3}
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
            placeholder="E.g., I’ve been offered a higher-paying job that would mean less time with my kids..."
            className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition leading-relaxed"
          />
        </div>
        <div>
          <label className="block text-sm text-[#6B6B6B] mb-2">What are you considering?</label>
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input
                  data-testid={`decision-option-${i}`}
                  value={o}
                  onChange={(e) => {
                    const next = [...options];
                    next[i] = e.target.value;
                    setOptions(next);
                  }}
                  placeholder={`Option ${i + 1}`}
                  className="flex-1 bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                />
                {options.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setOptions(options.filter((_, idx) => idx !== i))}
                    className="text-[#6B6B6B] hover:text-[#A86B4A] p-2"
                    aria-label="remove"
                    data-testid={`decision-remove-${i}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOptions([...options, ""])}
            data-testid="decision-add-option"
            className="mt-3 text-sm text-[#4F6F52] inline-flex items-center gap-1 hover:underline"
          >
            <Plus size={14} /> Add another option
          </button>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="decision-submit"
            disabled={busy}
            className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-3 text-sm font-medium inline-flex items-center gap-2 transition disabled:opacity-60 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
          >
            <Scale size={14} /> {busy ? "Weighing gently..." : "Get gentle wisdom"}
          </button>
        </div>
      </form>

      {busy && <div className="h-40 rounded-3xl shimmer" />}

      {r && (
        <div data-testid="decision-result" className="space-y-3 fade-in-up">
          <RiskBadge level={r.risk_level} />
          <div className="rounded-3xl border border-[#DCE5DB] bg-[#E1ECDD] p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
            <h3 className="font-display text-lg mb-2">A path to consider</h3>
            <p className="text-[#2E2E2E] font-medium leading-relaxed">{r.recommended_path}</p>
          </div>
          <div className="rounded-3xl border border-[#DCE5DB] bg-[#EEF3EC] p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
            <h3 className="font-display text-lg mb-2">Biblical perspective</h3>
            <p className="text-[15px] leading-[1.8] italic">{r.biblical_guidance}</p>
          </div>
          <div className="rounded-3xl border border-[#DCE5DB] bg-white p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
            <h3 className="font-display text-lg mb-2">Why this might be true</h3>
            <p className="text-[15px] leading-[1.8]">{r.reasoning}</p>
          </div>
        </div>
      )}
    </div>
  );
}
